// IMPORTING Injectable FROM ANGULAR CORE.
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

// IMPORTING ROUTER FOR REDIRECTION
import { Router } from '@angular/router';

// IMPORTING THE ANGULAR COMMON DATA.
import { HttpClient } from "@angular/common/http";

// IMPORTING THE ENVIRONMENT MODULE
import { environment } from "../../environments/environment";

// IMPORTING HELPER SERVICE
import { HelperService } from "./helper.service";


// INJECTING TO THE APP PROVIDER SO THAT WE CAN USE IT THROUGHOUT THE APPLICATION.
@Injectable
({
	providedIn: "root",
})

// DECLARING THE AuthService CLASS WITH EXPORT SO THAT WE CAN USE THIS CLASS IN OTHER FILES.
export class AuthService
{
	// GET THE API BASE.
	public apiBase = environment.apiURL;

	// STORE THE USER'S PROFILE INFORMATION IN THIS VARIABLE. IT WILL BE AVAILABLE IN WHOLE APP.
	public userInfo: any = null;
	public permissions: any = null; // STORE THE ARRAY OF PERMISSIONS OF THE LOGGED IN USER.
	public permissionsLoaded = new Subject<void>(); // EMITS WHEN PERMISSIONS ARE SET.

	/**
	 * SET WHEN SIGN-IN SAYS THIS ACCOUNT HAS NEVER CHOSEN A PASSWORD (mustsetpassword: 'Y').
	 *
	 * WHILE IT IS SET, AuthGuard SENDS EVERY OTHER PAGE TO THE SET-PASSWORD SCREEN. IN localStorage
	 * RATHER THAN A FIELD BECAUSE A RELOAD MUST NOT DROP IT - THE SERVER ONLY ANSWERS THE QUESTION
	 * AT SIGN-IN, SO A REFRESH WOULD OTHERWISE LET THE MEMBER STRAIGHT PAST.
	 */
	public static readonly MUST_SET_PASSWORD_KEY = 'mustSetPassword';

	// VARIABLE TO STORE USER INFO STATUSES.
	public userStatuses: any;

	// CLASS CONSTRUCTOR, THIS WILL BE THE FIRST FUNCTION TO BE EXECUTED WHEN THIS CLASS LOADS.
	// HERE WE WILL TELL ANGULAR TO INJECT A DEPENDENCY BY SPECIFYING A CONSTRUCTOR
	// PARAMETER WITH THE DEPENDENCY TYPE.
	constructor
	(
		private http: HttpClient,
		private helperService: HelperService,
		private router: Router,
	)
	{
	}

	/** Does this member still have to choose a password before they can use the portal? */
	mustSetPassword (): boolean
	{
		try
		{
			return localStorage.getItem (AuthService.MUST_SET_PASSWORD_KEY) === 'Y';
		}
		catch
		{
			// Storage can throw (private mode, blocked site data). Never lock the member out over it.
			return false;
		}
	}

	/** Remember, or forget, that this member owes us a password. */
	setMustSetPassword (must: boolean): void
	{
		try
		{
			if (must) localStorage.setItem (AuthService.MUST_SET_PASSWORD_KEY, 'Y');
			else localStorage.removeItem (AuthService.MUST_SET_PASSWORD_KEY);
		}
		catch
		{
			// As above: a portal that cannot write the flag still has to work.
		}
	}

	// SET THE PERMISSIONS FOR THE USER.
	setPermissions (permissions: any)
	{
		if (!permissions) {
			return;
		}

		permissions = permissions.map((p: string) => p.trim());

		// STORE PERMISSION IN LOCAL STORAGE.
		localStorage.setItem ('permissions', JSON.stringify(permissions));
		this.permissions = permissions;
		this.permissionsLoaded.next();
	}

	// CHECK IF USER HAS PERMISSION TO ACCESS THIS FEATURE?
	hasPermission (permission: string)
	{
		// IF PERMISSIONS ARE NOT LOADED THEN RETURN FALSE.
		if (!this.permissions)
		{
			if (localStorage.getItem ('permissions')) {
				let permissionStr: any = localStorage.getItem ('permissions');
				this.permissions = JSON.parse(permissionStr);
			} else {
				return false;
			}
		}

		// USER HAS "ALL" PERMISSION THEN RETURN TRUE.
		if (this.permissions.indexOf ('ALL') > -1)
		{
			return true;
		}

		// IF PERMISSIONS ARE LOADED THEN CHECK IF THE PERMISSION IS IN THE PERMISSIONS ARRAY.
		return (this.permissions.indexOf (permission) > -1);
	}

	// CALL THIS FUNCTION WITH USERNAME AND PASSWORD TO MAKE LOGIN.
	doLogin (username: string, password: string)
	{
		return this.http.post (this.apiBase + "/admin-sign-in", { username, password });
	}

	// CALL THIS FUNCTION FOR LOGIN CHECK
	isLoggedIn ()
	{
		let token = localStorage.getItem ("token");

		// BACKUP: Check sessionStorage if localStorage fails
		if (!token) {
			token = sessionStorage.getItem("token");
		}

		// BACKUP: Check memory fallback
		if (!token && (this as any).fallbackToken) {
			token = (this as any).fallbackToken;
		}

		return !!token;
	}

	// THIS FUNCTION WILL BE CALLED TO LOGOUT.
	logout (reason: string)
	{
		return new Promise ((resolve, reject) =>
		{
			// CALL THE API TO STORE THE LOGOUT REASON
			this.http.put (this.apiBase + `/logout`, {reason: reason}).subscribe((response: any) =>
			{
				// REMOVE THE LOCAL STORAGE
				localStorage.removeItem("token");
				localStorage.removeItem("permissions");
				localStorage.removeItem("userInfo");
				localStorage.removeItem("loginData");
				localStorage.removeItem(AuthService.MUST_SET_PASSWORD_KEY);

				// Also clear sessionStorage and memory fallback
				sessionStorage.removeItem("token");
				sessionStorage.removeItem("permissions");
				sessionStorage.removeItem("userInfo");

				// Clear memory fallback
				(this as any).fallbackToken = null;
				this.permissions = null;

				// REDIRECT TO LOGIN PAGE
				this.router.navigate(["/login"]);

				resolve(true);
			},
			(error: any) => {
				// IF API ERROR, STILL CLEAR LOCAL STORAGE AND REDIRECT
				localStorage.removeItem("token");
				localStorage.removeItem("permissions");
				localStorage.removeItem("userInfo");
				localStorage.removeItem("loginData");
				localStorage.removeItem(AuthService.MUST_SET_PASSWORD_KEY);

				// Also clear sessionStorage and memory fallback on error
				sessionStorage.removeItem("token");
				sessionStorage.removeItem("permissions");
				sessionStorage.removeItem("userInfo");
				(this as any).fallbackToken = null;
				this.permissions = null;

				this.router.navigate(["/login"]);
				resolve(false);
			});
		});
	}

	// THIS FUNCTION WILL BE CALLED TO GET THE USER'S PROFILE INFORMATION.
	async getUserInfo ()
	{
		return new Promise ((resolve, reject) =>
		{
			// CALL THE API TO GET THE LOAN DATA.
			this.http.get (this.apiBase + `/user-info?source=getUserInfo`).subscribe
			({
				next: (response: any) =>
				{
					// IF THE API RESPONSE IS SUCCESSFUL THEN SHOW THE DATA IN THE TABLE.
					if (response.success)
					{
						this.userInfo = response.data.user_info;
						this.helperService.userInfo = response.data.user_info;

						// SET USER STATUSES.
						this.userStatuses = response.data.statuses;

						// STORE USER INFO IN LOCAL STORAGE.
						localStorage.setItem ('userInfo', JSON.stringify(this.helperService.userInfo));
						this.setPermissions (this.userInfo?.permissions);
					}
					else // IF THE API RESPONSE IS NOT SUCCESSFUL THEN SHOW THE ERROR MESSAGE.
					{
						console.error ("Sorry, something went wrong. Please try again.");
					}
					resolve (this.userInfo);
				},
				error: (err) => // LOG THE ERROR.
				{
					console.error (err);
					reject (err);
				},
			});
		})
	}

	// CHECK IF THE USER IS AUTHORIZED TO ACCESS THE SPECIFIC ROUTE.
	async isAuthorized (route: string)
	{
		if (!this.permissions)
		{
			let permissionString: any = localStorage.getItem ('permissions');
			this.permissions = await JSON.parse(permissionString);
		}

		// CHECK PERMISSIONS IF USER IS TRYING TO ACCESS DASHBOARD.
		if (route.indexOf ("/dashboard") > -1)
		{
			return this.hasPermission ("VIEWLISTOFAPPLICATIONS");
		}

		// CHECK PERMISSIONS IF USER IS TRYING TO EDIT OR VIEW LOAN.
		else if (/^\/loan-application\/[a-f0-9-]+$/i.test(route))
		{
			return this.hasPermission ("VIEWLOANAPPLICATION") || this.hasPermission ("EDITLOANAPPLICATION");
		}

		// CHECK PERMISSIONS IF USER IS TRYING TO CREATE LOAN.
		else if (route.indexOf ("/loan-application") > -1)
		{
			return this.hasPermission ("CREATELOANAPPLICATION");
		}

		// CHECK PERMISSIONS IF USER IS TRYING TO ACCESS TRAINING ADMIN.
		else if (route.indexOf ("/training-admin") > -1)
		{
			return this.hasPermission ("VIEWTRAININGADMIN");
		}

		return true; // FOR ANYTHING ELSE, ALLOW THE USER TO ACCESS THE PAGE.
	}

	async sendOptMFA(email: string)
	{
		return new Promise ((resolve, reject) =>
		{
			// CALL THE API TO GET THE LOAN DATA.
			this.http.put (this.apiBase + `/send-otp-mfa`, { email: email }).subscribe
			({
				next: (response: any) =>
				{
					resolve (response);
				},
				error: (err) => // LOG THE ERROR.
				{
					console.error (err);
					reject (err);
				},
			});
		});
	}

	async verifyOptMFA(email: string, otp: any)
	{
		return new Promise ((resolve, reject) =>
		{
			// CALL THE API TO GET THE LOAN DATA.
			this.http.put (this.apiBase + `/verify-otp-mfa`, { email: email, otp: otp }).subscribe
			({
				next: (response: any) =>
				{
					resolve (response);
				},
				error: (err) => // LOG THE ERROR.
				{
					console.error (err);
					reject (err);
				},
			});
		});
	}
}
